import { Code } from '@common/Code';
import { NodeTrace } from '@store/slices';
import { Alert, Collapse, IconButton, Stack } from '@mui/material';
import { KeyboardArrowDownOutlined, KeyboardArrowUpOutlined } from '@mui/icons-material';
import { useTheme } from '../../styles';
import { getIcon, NodeIcon, NodeId } from '@common/Node/Defaults';
import { config } from '@components/Flow/Nodes';
import { useState } from 'react';
import { CardContainer, CardContent, CardHeader, ErrorContainer } from '@common/Node/CompletionChatbot/WorkflowTrace';

interface Props {
  trace: NodeTrace;
  index: number;
}

export const NodeExecution: React.FC<Props> = ({ trace, index }) => {
  const theme = useTheme();

  const [collapsed, setCollapsed] = useState(trace?.collapsed);

  const getBackgroundColor = (type: string, background?: string) => {
    if (type === 'unknown') return theme.background.dark;
    if (!background) return config?.[type]?.config?.background;
    return background;
  };

  const getTraceIcon = (type: string) => {
    const nodeIcon = config?.[type]?.config?.icon;
    if (!nodeIcon) return 'unknown';
    return nodeIcon;
  };

  const duration = trace?.metadata?.timers?.[0]?.duration || '0';
  const showTimer = !(trace.type.includes('workflow') || trace.type.includes('error'));
  const icon = getTraceIcon(trace?.type);
  const background = getBackgroundColor(trace?.type, trace?.background);

  const getOutput = () => {
    const noDataMessage = 'This node did not return any output data.';

    try {
      if (trace?.metadata?.output && Object.keys(trace.metadata.output).length === 0) return noDataMessage;
      return trace?.metadata?.output;
    } catch (e) {
      return trace?.metadata?.output || noDataMessage;
    }
  };

  return (
    <CardContainer>
      <CardHeader collapsed={collapsed}>
        <div className='row'>
          <div className='title'>
            {trace?.metadata?.node_id && <NodeId background={background}>{`#${trace.metadata.node_id}`}</NodeId>}
            <NodeIcon timer background={background}>
              {getIcon(icon)}
            </NodeIcon>
            <h4>{trace?.name !== 'unknown' ? trace.name : `Node ${index}`}</h4>
          </div>
          <Stack direction='row' spacing={1.5} alignItems='center'>
            {showTimer && (
              <NodeId timer background={theme.telnyx.purple}>
                {duration}
              </NodeId>
            )}
            {trace?.icon !== 'workflow' && (
              <IconButton
                size='small'
                sx={{ padding: '2px', borderRadius: '6px' }}
                onClick={() => setCollapsed((prevState) => !prevState)}
              >
                {!collapsed ? <KeyboardArrowUpOutlined /> : <KeyboardArrowDownOutlined />}
              </IconButton>
            )}
          </Stack>
        </div>
      </CardHeader>
      <Collapse in={!collapsed} timeout='auto' unmountOnExit>
        <div>
          {(trace.type.includes('error') || trace?.description || trace?.metadata?.output) && (
            <CardContent>
              {trace.metadata?.status === 'failed' ? (
                <ErrorContainer>
                  <Code noPadding code={trace.description} language='json' />
                </ErrorContainer>
              ) : trace?.metadata?.output ? (
                <ErrorContainer>
                  <Code noPadding code={getOutput()} language='json' />
                </ErrorContainer>
              ) : (
                trace.description
              )}
            </CardContent>
          )}
        </div>
        {!(trace.type.includes('error') || trace?.description || trace?.metadata?.output) && (
          <Stack padding='0 12px 12px 12px'>
            <Alert severity='info'>
              {trace?.metadata?.status === 'processing'
                ? 'This node is currently running.'
                : trace?.metadata?.status === 'pending' || !trace?.metadata?.status
                ? 'This node has not been executed yet.'
                : 'This node does not have output data.'}
            </Alert>
          </Stack>
        )}
      </Collapse>
    </CardContainer>
  );
};
